<template>
  <v-dialog v-model="dialog" width="600" persistent>
    <v-card style="border-radius: 10px">
      <v-card-title class="d-flex justify-space-between align-center">
        <span class="text-h6 font-weight-bold">Skills</span>
        <v-btn icon size="small" @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text>
        <div class="text-body-2 mb-4 text-grey-darken-1">
          Add your technical or soft skills such as Salesforce, Communication
          skills, etc.
        </div>

        <!-- Input Field -->
        <v-text-field
          v-model="inputSkill"
          label="Enter skills you use at work, e.g. ‘Good Communication’"
          hide-details
          solo
          flat
          dense
          clearable
          @keyup.enter="addSkill"
        />

        <!-- “Add ‘foo’” suggestion dropdown -->
        <v-list
          v-if="showSuggestion"
          class="elevation-2"
          style="margin-top: -8px"
        >
          <v-list-item @click="addSkill">
            <v-list-item-title>Add "{{ inputSkill }}"</v-list-item-title>
          </v-list-item>
        </v-list>

        <!-- Error message -->
        <div v-if="error" class="text-error text-caption mb-2">
          This field cannot be blank
        </div>

        <!-- Already‐added skill chips -->
        <div class="d-flex flex-wrap mt-2" style="gap: 8px">
          <v-chip
            v-for="(skill, index) in skills"
             :key="skill" 
            closable
            @click:close="removeSkill(index)"
            class="ma-2"
          >
            {{ skill }}
          </v-chip>
        </div>
      </v-card-text>

      <v-card-actions class="d-flex justify-end">
        <v-card-actions class="d-flex justify-end">
          <v-btn variant="outlined" @click="clearAll">Clear All</v-btn>
          <v-btn
            variant="flat"
            color="primary"
            :disabled="!canSave"
            @click="saveSkills"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, watch, computed } from "vue";
import { useStore } from "vuex";

// Mirror the parent’s v-model
const props = defineProps({ modelValue: Boolean });
const emit = defineEmits(["update:modelValue"]);

// Local “dialog open/close” ref
const dialog = ref(props.modelValue);
watch(
  () => props.modelValue,
  (val) => (dialog.value = val)
);
watch(dialog, (val) => emit("update:modelValue", val));

// Vuex store
const store = useStore();

// Local state for the input field and skill list
const inputSkill = ref("");
const skills = ref([]);

// Error flag if user tries to add an empty skill
const error = ref(false);

// Show the “Add ‘…’” suggestion only if input is non‐empty and not already in the list
const showSuggestion = computed(() => {
  const trimmed = inputSkill.value?.trim();
  return (
    trimmed &&
    !skills.value.some((s) => s.toLowerCase() === trimmed.toLowerCase())
  );
});

// Add a skill when user hits “Enter” or clicks the suggestion
const addSkill = () => {
  const trimmed = inputSkill.value?.trim();
  if (trimmed && !skills.value.includes(trimmed)) {
    skills.value.push(trimmed);
    inputSkill.value = "";
    error.value = false;
  } else if (!trimmed) {
    error.value = true;
  }
};

// Remove a skill chip
function removeSkill(skillText) {
  // Remove by value instead of by index
  const idx = skills.value.indexOf(skillText);
  if (idx !== -1) skills.value.splice(idx, 1);
}
// Clear everything
const clearAll = () => {
  skills.value = [];
  inputSkill.value = "";
  error.value = false;
};

// Computed: enable “Save” only if there is at least one skill
const canSave = computed(() => {
  return skills.value.length > 0;
});

// Save into Vuex and close dialog
const saveSkills = () => {
  if (!skills.value.length) {
    error.value = true;
    return;
  }
  // Dispatch to the profile module
  store.dispatch("profile/saveSkills", skills.value.slice());
  console.log("🟢 [profile.SaveSkills] =", store.state.profile.skills);
  // (Optional) show a toast/alert
  store.dispatch("alert/success", "Skills saved successfully");
  dialog.value = false;
};

// Close (just closes the dialog, without saving)
const close = () => {
  dialog.value = false;
};

// When the dialog opens, pre‐fill from store.state.profile.skills
watch(
  () => dialog.value,
  (isOpen) => {
    if (isOpen) {
      // Copy the existing skills array (if any) so we don't mutate store state directly
      const existing = store.state.profile.skills || [];
      skills.value = Array.isArray(existing) ? [...existing] : [];
      inputSkill.value = "";
      error.value = false;
    }
  }
);
</script>

<style scoped>
.text-error {
  color: red;
}
</style>
